/**
 * CustomerPathsCohort — visualizes top N most common customer journey paths to purchase.
 * Requires expert plan + active tracking integration.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Route,
  TrendingUp,
  DollarSign,
  Users,
  ArrowRight,
  Lock,
  ShoppingCart,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────

interface PathStep {
  channel: string;
  source: string | null;
}

interface CustomerPath {
  pathKey: string;
  steps: PathStep[];
  count: number;
  percentage: number;
  totalRevenue: number;
  avgRevenue: number;
}

interface CustomerPathsData {
  paths: CustomerPath[];
  totalConversions: number;
  analyzedVisitors: number;
  dateRange: { from: string; to: string };
}

// ── Channel Colors ─────────────────────────────────────

const CHANNEL_COLOR_MAP: Record<string, string> = {
  "Meta Ads": "bg-blue-500/15 text-blue-400 border-blue-500/20",
  Instagram: "bg-pink-500/15 text-pink-400 border-pink-500/20",
  "Google Ads": "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  YouTube: "bg-red-500/15 text-red-400 border-red-500/20",
  Email: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  Direct: "bg-white/[0.06] text-white/60 border-white/10",
  Organic: "bg-green-500/15 text-green-400 border-green-500/20",
  "Paid Social": "bg-indigo-500/15 text-indigo-400 border-indigo-500/20",
  "Paid Search": "bg-amber-500/15 text-amber-400 border-amber-500/20",
  Referral: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  TikTok: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/20",
  LinkedIn: "bg-sky-500/15 text-sky-400 border-sky-500/20",
  SMS: "bg-orange-500/15 text-orange-400 border-orange-500/20",
  Video: "bg-rose-500/15 text-rose-400 border-rose-500/20",
};

const DEFAULT_CHANNEL_STYLE = "bg-white/[0.06] text-white/60 border-white/10";

const getChannelStyle = (label: string): string =>
  CHANNEL_COLOR_MAP[label] ?? DEFAULT_CHANNEL_STYLE;

// ── Formatters ─────────────────────────────────────────

const formatCurrency = (value: number): string =>
  value === 0
    ? "—"
    : value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " zł";

const formatPercent = (value: number): string => (value === 0 ? "—" : value.toFixed(1) + "%");

// ── Days options ───────────────────────────────────────

const DAYS_OPTIONS = [
  { value: "7", labelKey: "dashboard.roasWorld.paths.last7d" },
  { value: "14", labelKey: "dashboard.roasWorld.paths.last14d" },
  { value: "30", labelKey: "dashboard.roasWorld.paths.last30d" },
  { value: "60", labelKey: "dashboard.roasWorld.paths.last60d" },
  { value: "90", labelKey: "dashboard.roasWorld.paths.last90d" },
] as const;

const CONVERSION_TYPE_OPTIONS = [
  { value: "purchase", labelKey: "dashboard.roasWorld.paths.convPurchase" },
  { value: "lead", labelKey: "dashboard.roasWorld.paths.convLead" },
  { value: "booked_call", labelKey: "dashboard.roasWorld.paths.convBooked" },
  { value: "qualified_call", labelKey: "dashboard.roasWorld.paths.convQualified" },
] as const;

// ── Step Badge Component ───────────────────────────────

function StepBadge({ label }: { label: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border ${getChannelStyle(label)}`}
    >
      {label}
    </span>
  );
}

// ── Path Row Component ─────────────────────────────────

function PathRow({ path, rank, maxCount }: { path: CustomerPath; rank: number; maxCount: number }) {
  const barWidth = maxCount > 0 ? (path.count / maxCount) * 100 : 0;
  const steps = path.pathKey.split(" → ");

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.04] transition-colors">
      {/* Rank + Path Flow */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-7 h-7 rounded-lg bg-[#ef3e29]/10 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-[#ef3e29]">#{rank}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <StepBadge label={step} />
              {idx < steps.length - 1 && <ArrowRight className="w-3 h-3 text-white/30 shrink-0" />}
            </div>
          ))}
          <ArrowRight className="w-3 h-3 text-emerald-500 shrink-0" />
          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border bg-emerald-100 text-emerald-700 border-emerald-200">
            <ShoppingCart className="w-3 h-3" />
            Konwersja
          </span>
        </div>
      </div>

      {/* Stats + Bar */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ef3e29] to-[#ef3e29]/60 rounded-full transition-all duration-500"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0 text-xs">
          <div className="flex items-center gap-1 text-white/70">
            <Users className="w-3 h-3 text-white/30" />
            <span className="font-medium">{path.count}</span>
            <span className="text-white/30">({formatPercent(path.percentage)})</span>
          </div>
          <div className="flex items-center gap-1 text-white/70">
            <DollarSign className="w-3 h-3 text-emerald-500" />
            <span className="font-medium">{formatCurrency(path.avgRevenue)}</span>
            <span className="text-white/30">śr.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Expert Gate Component ──────────────────────────────

function ExpertPlanGate() {
  return (
    <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
      <CardContent className="p-10 text-center">
        <Lock className="w-12 h-12 text-[#ef3e29]/40 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Ścieżki klienta — plan Expert</h3>
        <p className="text-sm text-white/60 max-w-md mx-auto mb-4">
          Analiza najczęstszych ścieżek zakupowych wymaga aktywnego trackingu (Custom Tracking Code)
          i jest dostępna tylko w planie Expert.
        </p>
        <p className="text-xs text-white/30">
          Upgrade do planu Expert aby odblokować tę funkcjonalność.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Main Component ─────────────────────────────────────

const DEFAULT_CONVERSION_TYPE: Record<string, string> = {
  ecommerce: "purchase",
  lead_gen_basic: "lead",
  lead_gen_advanced: "lead",
  saas: "lead",
  autowebinar_book_a_call: "booked_call",
  vsl_funnel: "lead",
};

interface CustomerPathsCohortProps {
  isExpertPlan: boolean;
  dashboardMode?: string;
}

export default function CustomerPathsCohort({
  isExpertPlan,
  dashboardMode,
}: CustomerPathsCohortProps) {
  const { t } = useTranslation();
  const [days, setDays] = useState("30");
  const [conversionType, setConversionType] = useState(
    DEFAULT_CONVERSION_TYPE[dashboardMode ?? ""] ?? "lead"
  );

  const { data, isLoading, error } = useQuery<CustomerPathsData>({
    queryKey: ["customer-paths", days, conversionType],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/tracking/customer-paths?days=${days}&conversionType=${conversionType}&limit=10`
      ),
    staleTime: 5 * 60 * 1000,
    enabled: isExpertPlan,
  });

  if (!isExpertPlan) return <ExpertPlanGate />;

  return (
    <div className="space-y-4">
      {/* Header + Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wider flex items-center gap-2">
          <Route className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.roasWorld.paths.title")}
        </h2>
        <div className="flex items-center gap-2">
          <Select value={conversionType} onValueChange={setConversionType}>
            <SelectTrigger className="w-[180px] h-8 bg-[#1a1614] border-[#2e2624] text-white/80 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1614] border-[#2e2624]">
              {CONVERSION_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-white/80 text-xs">
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px] h-8 bg-[#1a1614] border-[#2e2624] text-white/80 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1614] border-[#2e2624]">
              {DAYS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-white/80 text-xs">
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="w-3.5 h-3.5 text-[#ef3e29]" />
                <span className="text-[10px] text-white/50 uppercase tracking-wide">
                  {t("dashboard.roasWorld.paths.conversions")}
                </span>
              </div>
              <div className="text-heading-2 font-extrabold text-white">
                {data.totalConversions}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] text-white/50 uppercase tracking-wide">
                  {t("dashboard.roasWorld.paths.uniqueClients")}
                </span>
              </div>
              <div className="text-heading-2 font-extrabold text-white">
                {data.analyzedVisitors}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Route className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] text-white/50 uppercase tracking-wide">
                  {t("dashboard.roasWorld.paths.uniquePaths")}
                </span>
              </div>
              <div className="text-heading-2 font-extrabold text-white">{data.paths.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#ef3e29] animate-spin" />
          <span className="ml-3 text-white/50 text-sm">
            {t("dashboard.roasWorld.paths.analyzing")}
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <Card className="bg-red-500/10 border border-red-500/20 rounded-xl">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-red-400">{t("dashboard.roasWorld.paths.errorTitle")}</p>
            <p className="text-xs text-red-400/70 mt-1">
              {t("dashboard.roasWorld.paths.errorHint")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Paths List */}
      {data && data.paths.length > 0 && (
        <div className="space-y-2">
          {data.paths.map((path, idx) => (
            <PathRow key={path.pathKey} path={path} rank={idx + 1} maxCount={data.paths[0].count} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {data && data.paths.length === 0 && (
        <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <CardContent className="p-10 text-center">
            <Route className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-sm text-white/40">{t("dashboard.roasWorld.paths.noData")}</p>
            <p className="text-xs text-white/30 mt-1">
              {t("dashboard.roasWorld.paths.noDataHint")}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
